/**
 * Master Portfolio Dashboard (Phase 2 redesign).
 *
 * Renders when the user has toggled "All Properties" in the PropertySelector.
 * Reads exclusively from the new /api/portfolio/* endpoints — no MMT API,
 * no writes, no reuse of MMT-frozen files.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATE_PRESETS = [
  { key: '7',   label: 'Last 7 days',   days: 7 },
  { key: '30',  label: 'Last 30 days',  days: 30 },
  { key: '90',  label: 'Last 90 days',  days: 90 },
  { key: 'mtd', label: 'Month to date' },
];

function isoDaysAgo(days) {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function isoMonthStart() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

function computeRange(presetKey) {
  if (presetKey === 'mtd') return { from: isoMonthStart(), to: today() };
  const p = DATE_PRESETS.find(x => x.key === presetKey);
  return { from: isoDaysAgo(p ? p.days : 30), to: today() };
}

const currency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const percent  = (n) => `${Number(n || 0).toFixed(1)}%`;

export function PortfolioDashboard({ onDrillDownToBookings }) {
  const [preset, setPreset] = useState('30');
  const [cityFilter, setCityFilter] = useState('');
  const [otaFilter, setOtaFilter] = useState(''); // csv
  const [statusFilter, setStatusFilter] = useState(''); // csv
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [attention, setAttention] = useState(null);
  const [otaCoverage, setOtaCoverage] = useState(null);
  const [otaDrill, setOtaDrill] = useState(null); // 'mmt' | 'agoda' | 'yatra' | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const range = useMemo(() => computeRange(preset), [preset]);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    p.set('from_date', range.from);
    p.set('to_date',   range.to);
    if (cityFilter.trim())   p.set('city',   cityFilter.trim());
    if (otaFilter.trim())    p.set('ota',    otaFilter.trim());
    if (statusFilter.trim()) p.set('status', statusFilter.trim());
    return p.toString();
  }, [range, cityFilter, otaFilter, statusFilter]);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = buildParams();
      const [k, p, a, c] = await Promise.all([
        axios.get(`${API}/portfolio/kpis?${qs}`,       authHeader()),
        axios.get(`${API}/portfolio/properties?${qs}`, authHeader()),
        axios.get(`${API}/portfolio/attention`,        authHeader()),
        axios.get(`${API}/portfolio/ota-coverage`,     authHeader()),
      ]);
      setKpis(k?.data?.kpis || null);
      setRows(p?.data?.results || []);
      setAttention(a?.data || null);
      setOtaCoverage(c?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const drillTo = (extra = {}) => {
    if (onDrillDownToBookings) onDrillDownToBookings({ from: range.from, to: range.to, ...extra });
  };

  return (
    <div className="space-y-6" data-testid="portfolio-dashboard">
      {/* Global filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-white">
        <div>
          <div className="text-[11px] text-gray-500 mb-1">Date range</div>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="text-sm border rounded px-2 py-1.5 min-w-[140px]"
            data-testid="portfolio-date-preset"
          >
            {DATE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <div className="text-[10px] text-gray-400 mt-1">{range.from} → {range.to}</div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 mb-1">City</div>
          <input
            type="text" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
            placeholder="Mumbai" className="text-sm border rounded px-2 py-1.5 w-[140px]"
            data-testid="portfolio-city-filter"
          />
        </div>
        <div>
          <div className="text-[11px] text-gray-500 mb-1">OTA (csv)</div>
          <input
            type="text" value={otaFilter} onChange={(e) => setOtaFilter(e.target.value)}
            placeholder="MMT,Agoda" className="text-sm border rounded px-2 py-1.5 w-[150px]"
            data-testid="portfolio-ota-filter"
          />
        </div>
        <div>
          <div className="text-[11px] text-gray-500 mb-1">Status (csv)</div>
          <input
            type="text" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            placeholder="active" className="text-sm border rounded px-2 py-1.5 w-[130px]"
            data-testid="portfolio-status-filter"
          />
        </div>
        <button
          type="button" onClick={fetchAll}
          className="text-sm px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700"
          data-testid="portfolio-refresh-btn"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="p-3 border-l-4 border-red-500 bg-red-50 text-sm text-red-700" data-testid="portfolio-error">
          {error}
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="portfolio-kpis">
        <KpiCard testid="kpi-total-properties"   label="Total Properties"  value={kpis?.total_properties ?? '—'} />
        <KpiCard testid="kpi-active-properties"  label="Active"            value={kpis?.active_properties ?? '—'} />
        <KpiCard testid="kpi-bookings"           label="Bookings"          value={kpis?.bookings ?? '—'}
                 onClick={() => drillTo({})} />
        <KpiCard testid="kpi-room-nights"        label="Room Nights"       value={kpis?.room_nights ?? '—'} />
        <KpiCard testid="kpi-gross-revenue"      label="Gross Revenue"     value={currency(kpis?.gross_revenue)} />
        <KpiCard testid="kpi-net-revenue"        label="Net Revenue"       value={currency(kpis?.net_revenue)} />
        <KpiCard testid="kpi-cancellations"      label="Cancellations"     value={kpis?.cancellations ?? '—'}
                 onClick={() => drillTo({ status: 'cancelled' })} />
        <KpiCard testid="kpi-cancellation-rate"  label="Cancellation %"    value={percent(kpis?.cancellation_rate)} />
        <KpiCard testid="kpi-adr"                label="ADR"               value={currency(kpis?.adr)} />
      </div>

      {/* OTA Coverage */}
      {otaCoverage && (
        <div className="space-y-3" data-testid="portfolio-ota-coverage">
          <div className="text-sm font-semibold text-gray-800">OTA Coverage · properties onboarded per channel</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['mmt', 'agoda', 'yatra'].map((ota) => (
              <OtaCoverageCard
                key={ota}
                ota={ota}
                count={otaCoverage?.counts?.[ota] ?? 0}
                active={otaDrill === ota}
                onClick={() => setOtaDrill(otaDrill === ota ? null : ota)}
              />
            ))}
          </div>
          {otaDrill && (
            <div className="border rounded-lg bg-white overflow-hidden" data-testid={`ota-coverage-drilldown-${otaDrill}`}>
              <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800 flex justify-between items-center">
                <span className="uppercase">{otaDrill} · {(otaCoverage?.properties?.[otaDrill] || []).length} properties</span>
                <button
                  type="button" onClick={() => setOtaDrill(null)}
                  className="text-[11px] text-gray-500 hover:text-gray-800"
                  data-testid="ota-coverage-drilldown-close"
                >Close ✕</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">GRB ID</th>
                      <th className="px-3 py-2 text-left">Property</th>
                      <th className="px-3 py-2 text-left">City</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Imported From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(otaCoverage?.properties?.[otaDrill] || []).length === 0 && (
                      <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={6}>No properties onboarded via this channel.</td></tr>
                    )}
                    {(otaCoverage?.properties?.[otaDrill] || []).map((r) => (
                      <tr key={r.id} className="border-t hover:bg-gray-50" data-testid={`ota-coverage-row-${r.grb_id || r.id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.grb_id || '—'}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                        <td className="px-3 py-2">{r.city || '—'}</td>
                        <td className="px-3 py-2">{r.property_type || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{r.imported_from || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attention Required */}
      {attention && (
        <div className="p-4 border rounded-lg bg-white" data-testid="portfolio-attention">
          <div className="text-sm font-semibold text-gray-800 mb-2">Today&apos;s Operations · Attention Required</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            <AttentionPill color="red"    label="Sync failures (24h)"   count={attention.counts.sync_failures_24h}         testid="attn-sync-failures" />
            <AttentionPill color="red"    label="Webhook errors (24h)"  count={attention.counts.webhook_errors_24h}        testid="attn-webhook-errors" />
            <AttentionPill color="orange" label="Cancellations today"   count={attention.counts.cancellations_today}       testid="attn-cancellations" />
            <AttentionPill color="orange" label="Zero inventory"        count={attention.counts.zero_inventory_properties} testid="attn-zero-inventory" />
            <AttentionPill color="red"    label="Disconnected OTA"      count={attention.counts.disconnected_ota_properties} testid="attn-disconnected" />
            <AttentionPill color="orange" label="Listings incomplete"   count={attention.counts.incomplete_listings}       testid="attn-incomplete" />
          </div>
        </div>
      )}

      {/* Property table */}
      <div className="border rounded-lg bg-white overflow-hidden" data-testid="portfolio-property-table">
        <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800 flex justify-between items-center">
          <span>Properties · {rows.length}</span>
          <span className="text-[11px] text-gray-500">Click a row to open in Single-Property mode</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">GRB ID</th>
                <th className="px-3 py-2 text-left">Property</th>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-right">Rooms</th>
                <th className="px-3 py-2 text-right">Bookings</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Cancel %</th>
                <th className="px-3 py-2 text-right">ADR</th>
                <th className="px-3 py-2 text-left">OTAs</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Last Sync</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={11}>No properties match the current filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50" data-testid={`portfolio-row-${r.grb_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.grb_id || '—'}</td>
                  <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                  <td className="px-3 py-2">{r.city || '—'}</td>
                  <td className="px-3 py-2 text-right">{r.total_rooms}</td>
                  <td className="px-3 py-2 text-right">{r.bookings}</td>
                  <td className="px-3 py-2 text-right">{currency(r.gross_revenue)}</td>
                  <td className="px-3 py-2 text-right">{percent(r.cancellation_rate)}</td>
                  <td className="px-3 py-2 text-right">{currency(r.adr)}</td>
                  <td className="px-3 py-2">{(r.connected_otas || []).map(o => (
                    <span key={o} className="inline-block mr-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 uppercase">{o}</span>
                  ))}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">
                    {r.last_sync_at ? new Date(r.last_sync_at).toLocaleString() : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OtaCoverageCard({ ota, count, active, onClick }) {
  const styles = {
    mmt:   { label: 'MakeMyTrip', ring: 'ring-red-400',     dot: 'bg-red-500' },
    agoda: { label: 'Agoda',      ring: 'ring-indigo-400',  dot: 'bg-indigo-500' },
    yatra: { label: 'Yatra',      ring: 'ring-amber-400',   dot: 'bg-amber-500' },
  };
  const s = styles[ota] || { label: ota, ring: 'ring-gray-400', dot: 'bg-gray-500' };
  return (
    <div
      className={`p-4 border rounded-lg bg-white cursor-pointer hover:shadow transition-shadow ${active ? `ring-2 ${s.ring}` : ''}`}
      data-testid={`ota-coverage-card-${ota}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} />
          <span className="text-sm font-semibold text-gray-800 uppercase">{ota}</span>
          <span className="text-[11px] text-gray-400">{s.label}</span>
        </div>
        <span className="text-[10px] text-gray-400">{active ? 'Hide' : 'View'} ▾</span>
      </div>
      <div className="text-2xl font-semibold text-gray-800 mt-2" data-testid={`ota-coverage-count-${ota}`}>{count}</div>
      <div className="text-[11px] text-gray-500">properties onboarded</div>
    </div>
  );
}

function KpiCard({ label, value, testid, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`p-3 border rounded-lg bg-white ${clickable ? 'cursor-pointer hover:shadow' : ''}`}
      data-testid={testid}
      onClick={onClick}
    >
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-800 mt-1">{value}</div>
    </div>
  );
}

function AttentionPill({ label, count, color = 'gray', testid }) {
  const map = {
    red:    'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    gray:   'bg-gray-50 text-gray-700 border-gray-200',
  };
  const c = count || 0;
  return (
    <div className={`px-3 py-2 border rounded ${map[color]}`} data-testid={testid}>
      <div className="text-[10px] uppercase">{label}</div>
      <div className="text-lg font-semibold">{c}</div>
    </div>
  );
}
